import React, { useEffect, useState } from 'react';
import {
  Box,
  Icon,
  SidePanel,
  TabContent,
  TabGroup,
  TabItem,
  Typography,
} from '@saviynt/design-system';

import { type User } from '../../../services';
import { useInvertedLocalize } from '../../../utils/localization';

import { type EntitlementRow, type LocalizeFn } from './IncludedEntitlements';
import invertedMessages from './messages';

import './RuleSidePanel.css';

const NULL_TEXT = '-';

// Helper function to convert boolean accessStatus to string
const getAccessStatusText = (l: LocalizeFn, accessStatus?: boolean): string => {
  if (accessStatus === true) {
    return l('Present');
  }

  return l('Absent');
};

interface RuleSidePanelProps {
  isOpen: boolean;
  onClose: () => void;
  data: User | EntitlementRow | null;
  type: 'user' | 'entitlement';
}

function UserSidePanelContent({ user }: { user: User }) {
  const l = useInvertedLocalize(invertedMessages);

  return (
    <Box className='RuleSidePanel-content'>
      <Box className='RuleSidePanel-section'>
        <Typography kind='h4' className='RuleSidePanel-sectionTitle'>
          {l('Attributes')}
        </Typography>
      </Box>

      {/* User Information */}
      <Box className='RuleSidePanel-section'>
        <Box className='RuleSidePanel-field'>
          <Typography kind='body2' className='RuleSidePanel-fieldLabel'>
            {l('User')}
          </Typography>
          <Typography kind='body3' className='RuleSidePanel-fieldValue'>
            {/* Show available name parts or dash if both are null */}
            {[user.firstname, user.lastname].filter(Boolean).join(' ') ||
              NULL_TEXT}
          </Typography>
        </Box>

        <Box className='RuleSidePanel-field'>
          <Typography kind='body2' className='RuleSidePanel-fieldLabel'>
            {l('Username')}
          </Typography>
          <Typography kind='body3' className='RuleSidePanel-fieldValue'>
            {user.username ?? NULL_TEXT}
          </Typography>
        </Box>

        <Box className='RuleSidePanel-field'>
          <Typography kind='body2' className='RuleSidePanel-fieldLabel'>
            {l('Employee Type')}
          </Typography>
          <Typography kind='body3' className='RuleSidePanel-fieldValue'>
            {user.employeetype ?? NULL_TEXT}
          </Typography>
        </Box>

        <Box className='RuleSidePanel-field'>
          <Typography kind='body2' className='RuleSidePanel-fieldLabel'>
            {l('Job Title')}
          </Typography>
          <Typography kind='body3' className='RuleSidePanel-fieldValue'>
            {user.title || NULL_TEXT}
          </Typography>
        </Box>

        <Box className='RuleSidePanel-field'>
          <Typography kind='body2' className='RuleSidePanel-fieldLabel'>
            {l('Department')}
          </Typography>
          <Typography kind='body3' className='RuleSidePanel-fieldValue'>
            {user.departmentname ?? NULL_TEXT}
          </Typography>
        </Box>

        <Box className='RuleSidePanel-field'>
          <Typography kind='body2' className='RuleSidePanel-fieldLabel'>
            {l('Manager')}
          </Typography>
          <Typography kind='body3' className='RuleSidePanel-fieldValue'>
            {user.manager || NULL_TEXT}
          </Typography>
        </Box>

        <Box className='RuleSidePanel-field'>
          <Typography kind='body2' className='RuleSidePanel-fieldLabel'>
            {l('Owner')}
          </Typography>
          <Typography kind='body3' className='RuleSidePanel-fieldValue'>
            {user.owner ?? NULL_TEXT}
          </Typography>
        </Box>
      </Box>

      <Box className='RuleSidePanel-field'>
        <Typography kind='body2' className='RuleSidePanel-fieldLabel'>
          {l('Country')}
        </Typography>
        <Typography kind='body3' className='RuleSidePanel-fieldValue'>
          {user.country ?? NULL_TEXT}
        </Typography>
      </Box>

      <Box className='RuleSidePanel-field'>
        <Typography kind='body2' className='RuleSidePanel-fieldLabel'>
          {l('City')}
        </Typography>
        <Typography kind='body3' className='RuleSidePanel-fieldValue'>
          {user.city ?? NULL_TEXT}
        </Typography>
      </Box>

      <Box className='RuleSidePanel-field'>
        <Typography kind='body2' className='RuleSidePanel-fieldLabel'>
          {l('Cost Center')}
        </Typography>
        <Typography kind='body3' className='RuleSidePanel-fieldValue'>
          {user.costcenter ?? NULL_TEXT}
        </Typography>
      </Box>

      <Box className='RuleSidePanel-field'>
        <Typography kind='body2' className='RuleSidePanel-fieldLabel'>
          {l('Access Status')}
        </Typography>
        <Typography kind='body3' className='RuleSidePanel-fieldValue'>
          {getAccessStatusText(l, user.accessStatus)}
        </Typography>
      </Box>
    </Box>
  );
}

// Component for entitlement details content
function EntitlementSidePanelContent({
  entitlement,
}: {
  entitlement: EntitlementRow;
}) {
  const l = useInvertedLocalize(invertedMessages);

  return (
    <Box className='RuleSidePanel-content'>
      <Box className='RuleSidePanel-section'>
        <Typography kind='h4' className='RuleSidePanel-sectionTitle'>
          {l('Attributes')}
        </Typography>
      </Box>

      {/* Entitlement Information */}
      <Box className='RuleSidePanel-section'>
        <Box className='RuleSidePanel-field'>
          <Typography kind='body2' className='RuleSidePanel-fieldLabel'>
            {l('Entitlement Type')}
          </Typography>
          <Typography kind='body3' className='RuleSidePanel-fieldValue'>
            {entitlement.entitlementDisplayName ||
              entitlement.entitlementTypeName ||
              NULL_TEXT}
          </Typography>
        </Box>

        <Box className='RuleSidePanel-field'>
          <Typography kind='body2' className='RuleSidePanel-fieldLabel'>
            {l('Entitlement Description')}
          </Typography>
          <Typography kind='body3' className='RuleSidePanel-fieldValue'>
            {entitlement.evDescription ?? NULL_TEXT}
          </Typography>
        </Box>

        <Box className='RuleSidePanel-field'>
          <Typography kind='body2' className='RuleSidePanel-fieldLabel'>
            {l('Entitlement Glossary')}
          </Typography>
          <Typography kind='body3' className='RuleSidePanel-fieldValue'>
            {entitlement.entitlementGlossary ?? NULL_TEXT}
          </Typography>
        </Box>

        <Box className='RuleSidePanel-field'>
          <Typography kind='body2' className='RuleSidePanel-fieldLabel'>
            {l('Entitlement Privileged')}
          </Typography>
          <Typography kind='body3' className='RuleSidePanel-fieldValue'>
            {entitlement.entitlementPrivileged ?? NULL_TEXT}
          </Typography>
        </Box>

        <Box className='RuleSidePanel-field'>
          <Typography kind='body2' className='RuleSidePanel-fieldLabel'>
            {l('Endpoint Name')}
          </Typography>
          <Typography kind='body3' className='RuleSidePanel-fieldValue'>
            {entitlement.endpointDisplayName ??
              (entitlement.endpointName || NULL_TEXT)}
          </Typography>
        </Box>

        <Box className='RuleSidePanel-field'>
          <Typography kind='body2' className='RuleSidePanel-fieldLabel'>
            {l('System Name')}
          </Typography>
          <Typography kind='body3' className='RuleSidePanel-fieldValue'>
            {entitlement.securitySystemDisplayName ??
              entitlement.securitySystemName ??
              NULL_TEXT}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}

function RuleSidePanel({ isOpen, onClose, data, type }: RuleSidePanelProps) {
  const l = useInvertedLocalize(invertedMessages);
  const [activeTab, setActiveTab] = useState('summary');

  useEffect(() => {
    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen) {
        event.preventDefault();
        event.stopPropagation();
        onClose();
      }
    };

    if (isOpen) {
      // Use capture phase to ensure it runs before child handlers
      document.addEventListener('keydown', handleEscapeKey, true);
    }

    return () => {
      document.removeEventListener('keydown', handleEscapeKey, true);
    };
  }, [isOpen, onClose]);

  if (!data) {
    return null;
  }

  // side panel header config
  const getHeaderConfig = () => {
    if (type === 'user') {
      const user = data as User;

      // if firstname and lastname are both null, show username
      return {
        title:
          [user.firstname, user.lastname].filter(Boolean).join(' ') ||
          user.username,
        description: l('User'),
        headerIcon: <Icon kind='User' size='xxLarge' />,
      };
    }

    const entitlement = data as EntitlementRow;

    return {
      title: entitlement.evDisplayName ?? entitlement.entitlementValueName,
      description: entitlement.evDescription ?? '-',
      headerIcon: (
        <Icon
          kind='Entitlement'
          size='xxLarge'
          className='RuleSidePanel-headerIcon'
        />
      ),
    };
  };

  const layout = (
    <div className='RuleSidePanel-layoutWidth'>
      <TabGroup value={activeTab} onChange={setActiveTab}>
        <TabItem
          label={l('Summary')}
          value='summary'
          icon={<Icon kind='Document' size='smallMedium' />}
        />
      </TabGroup>
      <TabContent value={activeTab} tabValue='summary'>
        {type === 'user' ? (
          <UserSidePanelContent user={data as User} />
        ) : (
          <EntitlementSidePanelContent entitlement={data as EntitlementRow} />
        )}
      </TabContent>
    </div>
  );

  return (
    <SidePanel
      isOpen={isOpen}
      onClose={onClose}
      header={getHeaderConfig()}
      layout={layout}
      className='RuleSidePanel'
      classNames={{ layoutClassName: 'RuleSidePanel-layout' }}
    />
  );
}

export default RuleSidePanel;
